import { useState } from 'react';
import { useScrollRevealGroup } from '../../../hooks/useScrollReveal';

const faqs = [
  {
    id: 'specialise',
    tag: 'Expertise',
    q: 'Do you specialise in tourism website design in Australia?',
    a: 'Yes — tourism is our core specialisation. Every one of our active clients is a tourism operator: reef trip companies, ferry services, dive centres, and adventure charters across Far North Queensland and regional Australia. We understand seasonal demand patterns, aggregator competition, and how to win bookings from travellers searching on mobile.',
  },
  {
    id: 'google-ranking',
    tag: 'SEO',
    q: 'Will my tourism website rank on Google?',
    a: 'We build SEO into every tourism website from day one — clean code structure, keyword-optimised content, schema markup, and local citation signals. Mission Beach Dive went from zero Google presence to #1 ranking in 52 days. Dunk Island Ferry claimed the top spot for core terms before their first season was complete.',
  },
  {
    id: 'ai-search',
    tag: 'AI Search',
    q: 'Can my tourism business appear in ChatGPT and Gemini AI results?',
    a: "Yes. We build GEO (Generative Engine Optimisation) into our tourism websites as standard — structuring your content, schema, and authority signals so that when a traveller asks ChatGPT 'who does reef trips near Mission Beach?' your business gets recommended. Mission Beach Adventures is now cited in ChatGPT and Google Gemini results.",
  },
  {
    id: 'booking',
    tag: 'Bookings',
    q: 'Do you integrate online booking systems?',
    a: 'Yes. We integrate your existing booking platform (Rezdy, FareHarbor, Booking.com, Airbnb, or direct booking systems) into the website design so travellers can book without leaving your site. We also advise on reducing aggregator dependency and increasing direct bookings — which keeps more margin in your business.',
  },
  {
    id: 'timeline',
    tag: 'Timeline',
    q: 'How long does a tourism website take to build?',
    a: 'Most tourism websites are completed and launched within 3–5 weeks from the initial brief. We prioritise getting you live before your key season, then continue optimising for search and AI visibility after launch. Rush timelines are available for seasonal launches.',
  },
  {
    id: 'outside-qld',
    tag: 'Location',
    q: 'Do you work with tourism businesses outside Queensland?',
    a: "Absolutely. While we are based in Mission Beach and have deep roots in Far North Queensland tourism, we work with tourism and hospitality businesses all across Australia entirely remotely. Location is no barrier — our process is fully online from brief to launch.",
  },
];

export default function TourismFaqSection() {
  const [openId, setOpenId] = useState<string>('specialise');
  const groupRef = useScrollRevealGroup();

  return (
    <section id="tourism-faq" className="relative pt-24 pb-16 bg-ds-bg-2 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-ds-border to-transparent" />
      <div className="absolute inset-0 hero-grid opacity-10 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6" ref={groupRef}>
        {/* Header */}
        <div className="mb-14 reveal">
          <span className="section-label">Frequently Asked Questions</span>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mt-3">
            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl text-white leading-none">
              QUESTIONS ABOUT
              <br />
              TOURISM WEBSITE
              <br />
              <span className="text-ds-red neon-text-red">DESIGN.</span>
            </h2>
            <p className="font-body text-white/50 text-sm leading-relaxed max-w-xs">
              Straight answers. If your question isn&apos;t here, call us on 1300 029 054 or send an email — we respond same day.
            </p>
          </div>
          <div className="mt-8 h-px w-24 bg-ds-red" style={{ boxShadow: '0 0 12px rgba(180,255,57,0.4)' }} />
        </div>

        {/* Accordion */}
        <div className="space-y-px reveal">
          {faqs.map((faq) => {
            const isOpen = openId === faq.id;
            return (
              <div
                key={faq.id}
                className={`border transition-all duration-300 ${isOpen ? 'border-ds-red/40 bg-ds-bg' : 'border-ds-border bg-ds-bg hover:border-white/15'}`}
                style={isOpen ? { boxShadow: '0 0 24px rgba(180,255,57,0.05)' } : {}}
              >
                <button
                  onClick={() => setOpenId(isOpen ? '' : faq.id)}
                  className="w-full flex items-start gap-5 p-6 text-left cursor-pointer"
                >
                  <span
                    className={`inline-block border px-2 py-0.5 font-body text-xs tracking-widest uppercase flex-shrink-0 mt-0.5 transition-colors duration-300 ${isOpen ? 'border-ds-red/40 text-ds-red' : 'border-ds-border text-white/30'}`}
                  >
                    {faq.tag}
                  </span>
                  <p className={`font-display text-sm tracking-wide leading-snug flex-1 transition-colors duration-300 ${isOpen ? 'text-white' : 'text-white/60'}`}>
                    {faq.q}
                  </p>
                  <div
                    className={`w-6 h-6 flex items-center justify-center flex-shrink-0 border transition-all duration-300 ${isOpen ? 'border-ds-red/50 rotate-45' : 'border-ds-border'}`}
                  >
                    <i className="ri-add-line text-white/60 text-sm" />
                  </div>
                </button>
                <div
                  className="overflow-hidden transition-all duration-300"
                  style={{ maxHeight: isOpen ? '400px' : '0' }}
                >
                  <p className="font-body text-white/55 text-sm leading-relaxed px-6 pb-6 pl-[calc(1.5rem+2.5rem+1.25rem)]">
                    {faq.a}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA strip */}
        <div
          className="mt-12 border border-ds-border bg-ds-bg p-8 flex flex-col md:flex-row items-start justify-between gap-6 reveal"
          style={{ borderLeft: '3px solid #B4FF39' }}
        >
          <div>
            <h3 className="font-display text-xl text-white mb-1 tracking-wide">READY TO TALK ABOUT YOUR TOURISM WEBSITE?</h3>
            <p className="font-body text-white/45 text-sm">Free consultation. We&apos;ll tell you exactly what your site needs and what it would cost.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
            <a href="tel:1300029054" className="btn-outline text-xs flex items-center gap-2 whitespace-nowrap">
              <i className="ri-phone-line" />
              1300 029 054
            </a>
            <a href="mailto:hello@darksidedigital.com.au" className="btn-red text-xs whitespace-nowrap">
              Email Us
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
